import { useState } from "react"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Settings, Gamepad2, Trophy, Award, Calendar, Store, BarChart3 } from "lucide-react"
import Layout from "@/components/Layout/Layout"
import { GamesManagement } from "@/components/admin/GamesManagement"
import { TournamentTypesManagement } from "@/components/admin/TournamentTypesManagement"
import { FormatsManagement } from "@/components/admin/FormatsManagement"
import { SeasonsManagement } from "@/components/admin/SeasonsManagement"
import { StoresManagement } from "@/components/admin/StoresManagement"
import { ReportsManagement } from "@/components/admin/ReportsManagement"
import { useAuthContext } from "@/contexts/AuthContext"
import NotFound from "./NotFound"
import { LoadingScreen } from "@/components/ui/loading-spinner"
import { Link, useParams } from "react-router-dom"

export function Administration() {
  const { section } = useParams<{ section: string }>()
  const { isAuthenticated, isAdmin, loading } = useAuthContext()
  const [activeTab, setActiveTab] = useState(section || "stores")
  const adminSections = [
    {
      id: "stores",
      title: "Tiendas",
      description: "Gestionar tiendas del sistema",
      icon: Store,
      component: StoresManagement,
    },
    {
      id: "games",
      title: "Juegos",
      description: "Gestionar juegos disponibles",
      icon: Gamepad2,
      component: GamesManagement,
    },
    {
      id: "tournament-types",
      title: "Tipos de Torneos",
      description: "Configurar tipos de torneo",
      icon: Trophy,
      component: TournamentTypesManagement,
    },
    {
      id: "formats",
      title: "Formatos de Juego",
      description: "Administrar formatos",
      icon: Award,
      component: FormatsManagement,
    },
    {
      id: "seasons",
      title: "Temporadas",
      description: "Gestionar temporadas de juego",
      icon: Calendar,
      component: SeasonsManagement,
    },
    {
      id: "reports",
      title: "Reportes",
      description: "Reportes y estadísticas",
      icon: BarChart3,
      component: ReportsManagement,
    },
  ]

  if (loading) {
    return <LoadingScreen />
  }

  if ((isAuthenticated && !isAdmin) || !isAuthenticated) {
    return <NotFound />
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between mb-4">
            <div className="flex items-center gap-3">
              <Settings className="h-8 w-8 text-primary" />
              <h1 className="text-3xl font-bold text-foreground">Administración del Sistema</h1>
            </div>

            <Button asChild variant="outline">
              <Link to="/admin/player-stats">Ir a Stats de Jugador</Link>
            </Button>
          </div>
          <p className="text-lg text-muted-foreground">
            Gestiona los elementos principales del sistema de torneos
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-8">
          {adminSections.map((section) => (
            <Card
              key={section.id}
              className={`cursor-pointer transition-all duration-200 hover:shadow-lg ${
                activeTab === section.id ? "ring-2 ring-primary bg-primary/5" : "border-border"
              }`}
              onClick={() => setActiveTab(section.id)}
            >
              <CardHeader className="pb-3">
                <div className="flex items-center gap-3">
                  <section.icon className="h-6 w-6 text-primary" />
                  <CardTitle className="text-lg">{section.title}</CardTitle>
                </div>
                <CardDescription className="text-sm">{section.description}</CardDescription>
              </CardHeader>
            </Card>
          ))}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="hidden">
            {adminSections.map((section) => (
              <TabsTrigger key={section.id} value={section.id}>
                {section.title}
              </TabsTrigger>
            ))}
          </TabsList>

          {adminSections.map((section) => (
            <TabsContent key={section.id} value={section.id} className="mt-0">
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <section.icon className="h-6 w-6 text-primary" />
                    <div>
                      <CardTitle className="text-2xl">{section.title}</CardTitle>
                      <CardDescription className="text-base">{section.description}</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <section.component />
                </CardContent>
              </Card>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </Layout>
  )
}
